/**
 * @file Bridge通信实现文件
 * @description 实现了与Flutter通信的核心功能
 */

import { BRIDGE_HANDLER, BRIDGE_METHODS } from './constants'

/** Bridge响应结构 */
export interface BridgeResponse<T = unknown> {
  responseId: string
  result: {
    code: number
    data: T
    errorMsg?: string
  }
}

/** Bridge请求结构 */
interface BridgeRequest {
  methodName: string
  data?: unknown
}

/** 回调函数类型 */
type CallbackFn = (response: BridgeResponse) => void

/** 左滑返回处理器配置 */
export interface SwipeBackConfig {
  /** 需要显示确认弹窗的路径匹配规则 */
  confirmPaths?: string[]
  /** 确认弹窗回调 */
  onConfirm?: () => void
}

/** 扩展 Window 接口 */
declare global {
  interface Window {
    flutter_inappwebview?: {
      callHandler: (handler: string, request: BridgeRequest) => Promise<BridgeResponse<unknown>>
    }
    jsCallback?: (response: BridgeResponse) => void
    onBackPressed?: () => void
  }
}

/**
 * Bridge服务类
 * @description 实现了与Flutter通信的所有方法，使用单例模式
 */
class BridgeService {
  private static instance: BridgeService | null = null
  /** 存储回调函数的Map */
  private callbacks: Map<string, CallbackFn>
  /** Bridge是否初始化完成的标志 */
  private isReady: boolean
  /** 等待初始化完成的回调函数数组 */
  private readyCallbacks: Array<() => void>
  /** 左滑返回配置 */
  private swipeBackConfig: SwipeBackConfig

  /**
   * 私有构造函数，初始化属性并开始监听
   */
  private constructor() {
    this.callbacks = new Map()
    this.isReady = false
    this.readyCallbacks = []
    this.swipeBackConfig = {}
    this.initialize()
  }

  /**
   * 获取BridgeService实例
   * @returns BridgeService的单例
   */
  static getInstance(): BridgeService {
    if (!BridgeService.instance) {
      BridgeService.instance = new BridgeService()
    }
    return BridgeService.instance
  }

  /**
   * 配置左滑返回行为
   * @param config - 配置项
   */
  configureSwipeBack(config: SwipeBackConfig): void {
    this.swipeBackConfig = { ...this.swipeBackConfig, ...config }
  }

  /**
   * 初始化Bridge服务
   */
  private initialize(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupBridge())
    } else {
      this.setupBridge()
    }
  }

  /**
   * 设置 Bridge 监听和回调
   */
  private setupBridge(): void {
    // 先检查是否已经触发过事件
    if (window.flutter_inappwebview) {
      console.log('Flutter WebView 已就绪')
      this.isReady = true
      this.readyCallbacks.forEach((callback) => callback())
      this.readyCallbacks = []
    }

    // 监听 Flutter WebView 初始化完成事件
    window.addEventListener('flutterInAppWebViewPlatformReady', () => {
      console.log('Flutter WebView 初始化完成')
      this.isReady = true
      this.readyCallbacks.forEach((callback) => callback())
      this.readyCallbacks = []
    })

    // 注册全局回调函数，供 Flutter 调用
    window.jsCallback = (response: BridgeResponse) => {
      const callback = this.callbacks.get(response.responseId)
      if (callback) {
        callback(response)
        this.callbacks.delete(response.responseId)
      }
    }

    // 注册全局左滑返回处理函数，供 Flutter 调用
    window.onBackPressed = () => {
      this.handleSwipeBack()
    }
  }

  /**
   * 确保Bridge已经初始化完成
   * @returns 初始化完成的Promise
   */
  private ensureReady(): Promise<void> {
    if (this.isReady) {
      return Promise.resolve()
    }
    return new Promise((resolve) => {
      this.readyCallbacks.push(resolve)
    })
  }

  /**
   * 调用无返回值的Flutter方法
   * @param methodName - 方法名
   * @param data - 可选的参数数据
   */
  async invoke(methodName: string, data?: unknown): Promise<void> {
    await this.ensureReady()
    const request: BridgeRequest = {
      methodName,
      data
    }
    window.flutter_inappwebview?.callHandler(BRIDGE_HANDLER, request)
  }

  /**
   * 调用返回值的Flutter方法
   * @param methodName - 方法名
   * @param data - 可选的参数数据
   * @returns 返回Promise包装的结果
   */
  async invokeSync<T = unknown>(methodName: string, data?: unknown): Promise<BridgeResponse<T>> {
    await this.ensureReady()
    const request: BridgeRequest = {
      methodName,
      data
    }
    return window.flutter_inappwebview?.callHandler(BRIDGE_HANDLER, request) as Promise<
      BridgeResponse<T>
    >
  }

  /**
   * 处理左滑返回事件
   */
  private handleSwipeBack(): void {
    const currentPath = window.location.pathname
    const currentHash = window.location.hash

    console.log('左滑返回触发，当前路由:', currentPath, currentHash)

    const { confirmPaths = [], onConfirm } = this.swipeBackConfig

    // 检查是否需要显示确认弹窗
    const needsConfirm = confirmPaths.some(
      (path) => currentPath.includes(path) || currentHash.includes(path)
    )

    if (needsConfirm && onConfirm) {
      onConfirm()
    } else {
      // 其他页面：默认返回
      this.invoke(BRIDGE_METHODS.GO_BACK)
    }
  }
}

// 导出Bridge服务实例
export const bridge = BridgeService.getInstance()
